import {
  vArray,
  vDescription,
  vMaxLength,
  vMinLength,
  vNumber,
  vObject,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
} from '@fv/models/validation'

import { aiModels } from '../ai.ts'
import { adminReportFiltersSchema } from '../report-filters.ts'

const improvementSchema = vObject({
  title: vPipe(
    vString(),
    vDescription(
      'A short, actionable title for the improvement (e.g. "CSV export for shipment data")',
    ),
  ),
  description: vPipe(
    vString(),
    vDescription(
      'What users are asking for, grouped into a single coherent description',
    ),
  ),
  category: vPipe(
    vPicklist([
      'feature-request',
      'ux-friction',
      'bug-report',
      'how-to',
      'other',
    ]),
    vDescription(
      'The primary category for this improvement (e.g. "feature-request", "ux-friction", "bug-report", "how-to", "other").',
    ),
  ),
  instanceCount: vPipe(
    vNumber(),
    vDescription('How many analyzed chats surfaced this improvement'),
  ),
  severity: vPipe(
    vPicklist(['high', 'medium', 'low']),
    vDescription(
      'Severity based on user impact and frequency: high = frequent pain point, medium = notable friction, low = nice-to-have',
    ),
  ),
  relatedFeedback: vPipe(
    vArray(vString()),
    vDescription(
      'The raw missingCapabilities and feedbackTopics that were clustered into this bucket',
    ),
  ),
  chatIds: vPipe(
    vArray(vString()),
    vMaxLength(10),
    vMinLength(1),
    vDescription(
      'A sampling of 10 or fewer chat IDs that contributed to this improvement, taken from the chatId field in the input data',
    ),
  ),
  actionItems: vPipe(
    vArray(vString()),
    vDescription(
      'Specific, granular next steps a product or engineering team could take to address this improvement (e.g. "Add date-range filter to carrier performance tool", "Surface PO number in shipment detail lookup results")',
    ),
  ),
})

const sentimentBreakdownSchema = vObject({
  positive: vNumber(),
  neutral: vNumber(),
  negative: vNumber(),
})

export const chatAggregationResultSchema = vObject({
  improvements: vPipe(
    vArray(improvementSchema),
    vDescription(
      'Suggested improvements ranked by instance count descending. Similar feedback should be bucketed together.',
    ),
  ),
  sentimentBreakdown: vPipe(
    sentimentBreakdownSchema,
    vDescription('Count of chats by sentiment across the analyzed period'),
  ),
  totalChatsAnalyzed: vPipe(
    vNumber(),
    vDescription('Total number of chat analyses included in this aggregation'),
  ),
  overallSummary: vPipe(
    vString(),
    vDescription(
      'A 2-3 sentence executive summary of the overall feedback trends and top priorities',
    ),
  ),
})

export type ChatAggregationResult = vOutput<typeof chatAggregationResultSchema>

export const chatAggregationFiltersSchema = vObject({
  ...adminReportFiltersSchema.entries,
  aiModel: vOptional(vPicklist(aiModels)),
})
export type ChatAggregationFilters = vOutput<
  typeof chatAggregationFiltersSchema
>
